import React, { useEffect, useState } from 'react';
import { getSettlement } from '../../api/system/system.api';

/**
 * 채널 1개의 정산 이력 표 — 고객사 상세(`ChannelDetailAdmin`)와 정산관리(`SettlementAdmin`)가 같이 쓴다(2026-10-01).
 *
 * 정산서 칸(상태 + 확정 시각) · 결제 칸(완료/미결제/실패) · 행 클릭 시 그 정산서 항목 펼침. 규칙은 한 곳에만 둔다 —
 * 두 화면이 서로 다른 표를 그리면 같은 정산서를 두 화면에서 다르게 읽게 된다.
 *
 * @param channelId     채널 id. 바뀌면 펼침·캐시를 비운다(앞 채널 내역이 남으면 안 된다).
 * @param history       `listSettlementHistory(channelId).data`(최근순)
 * @param highlightMonth (선택) 지금 작업 중인 이용월 — 행을 노란색으로 강조한다(정산관리에서 연 초안).
 * @param compareMonth   (선택) 비교 기준 이용월 — '전월' 배지를 단다.
 */

const won = (n) => (n ?? 0).toLocaleString('ko-KR');

const SETTLEMENT_STATUS_BADGE = {
  DRAFT: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  AUTO_CONFIRMED: 'bg-green-100 text-green-800',
  CANCELED: 'bg-gray-200 text-gray-500',
};

const SETTLEMENT_STATUS_LABEL = {
  DRAFT: '초안',
  CONFIRMED: '확정',
  AUTO_CONFIRMED: '자동 확정',
  CANCELED: '취소',
};

const PAYMENT_METHOD_LABEL = { CARD: '카드', TRANSFER: '계좌 입금' };

/**
 * 정산 이력의 '결제' 칸 — `settlement.paid_at` 기준(2026-10-01).
 * 확정(CONFIRMED)은 금액이 동결됐다는 뜻일 뿐 **돈이 들어왔다는 뜻이 아니다.** 둘을 한 칸에 두면 미수금을 놓친다.
 */
const paymentView = (h) => {
  const method = PAYMENT_METHOD_LABEL[h.paymentMethod] ?? h.paymentMethod ?? '';
  if (h.paidAt) {
    return {
      label: '결제 완료',
      cls: 'bg-blue-100 text-blue-800',
      sub: [method, h.paidAt].filter(Boolean).join(' · '),
      title: h.paymentFailCount ? `카드 결제 실패 ${h.paymentFailCount}회 뒤 결제됨` : undefined,
    };
  }
  if (h.status === 'CANCELED') return { label: '—', cls: 'text-gray-400', sub: '' };
  if (h.status !== 'CONFIRMED' && h.status !== 'AUTO_CONFIRMED') {
    return { label: '확정 전', cls: 'bg-gray-100 text-gray-500', sub: '' };
  }
  if ((h.total ?? 0) <= 0) return { label: '청구액 없음', cls: 'bg-gray-100 text-gray-500', sub: '' };
  if (h.lastPaymentFailedAt) {
    return {
      label: `결제 실패 ${h.paymentFailCount || 1}회`,
      cls: 'bg-red-100 text-red-700',
      sub: `마지막 ${h.lastPaymentFailedAt}`,
      title: h.lastPaymentError ?? undefined,
    };
  }
  return { label: '미결제', cls: 'bg-amber-100 text-amber-800', sub: '' };
};

const ITEM_CATEGORY_LABEL = {
  MONTHLY: '월정액',
  ACQUISITION: '가입(획득) 과금',
  MESSAGE: '메시지 발송',
  MOMENT: '카카오 모먼트',
  GIFT: '카카오 선물하기',
  ONE_TIME: '일회성',
  ETC: '기타',
};

/** 정산 이력 행을 펼쳤을 때의 정산서 항목(읽기 전용). state = { loading, data, error } */
const SettlementDetail = ({ state }) => {
  if (!state || state.loading) return <div className="text-xs text-gray-500">불러오는 중…</div>;
  if (state.error) return <div className="text-xs text-red-600">정산서를 불러오지 못했습니다: {state.error}</div>;
  const d = state.data;
  if (!d) return <div className="text-xs text-gray-500">정산서가 없습니다.</div>;
  const items = d.items ?? [];
  return (
    <div className="text-sm">
      {d.status === 'DRAFT' && (
        <div className="text-xs text-amber-700 mb-1">초안입니다 — 구독·비용을 바꿨다면 재생성 전까지 반영되지 않습니다.</div>
      )}
      <table className="min-w-full text-xs border bg-white">
        <thead className="bg-gray-100">
          <tr className="divide-x">
            <th className="px-2 py-1 text-left">구분</th>
            <th className="px-2 py-1 text-left">항목</th>
            <th className="px-2 py-1 text-left">설명</th>
            <th className="px-2 py-1 text-left">이용 기간</th>
            <th className="px-2 py-1 text-right">단가</th>
            <th className="px-2 py-1 text-right">수량</th>
            <th className="px-2 py-1 text-right">공급가</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={7} className="px-2 py-2 text-center text-gray-400">항목이 없습니다.</td></tr>}
          {items.map((it, i) => (
            <tr key={it.id ?? i} className="border-t divide-x">
              <td className="px-2 py-1 whitespace-nowrap">{ITEM_CATEGORY_LABEL[it.category] ?? it.category}</td>
              <td className="px-2 py-1">
                {it.name}
                {it.isManual && <span className="ml-1 text-[10px] text-gray-400">수기</span>}
                {it.vatExempt && <span className="ml-1 text-[10px] text-gray-400">면세</span>}
              </td>
              <td className="px-2 py-1 text-gray-500">{it.description ?? ''}</td>
              <td className="px-2 py-1 whitespace-nowrap text-gray-500">
                {it.coverageFrom ? `${it.coverageFrom} ~ ${it.coverageTo ?? ''}` : (it.coverageMonth ?? '')}
              </td>
              <td className="px-2 py-1 text-right">{won(it.unitPriceNet)}</td>
              <td className="px-2 py-1 text-right">{won(it.quantity)}</td>
              <td className="px-2 py-1 text-right">{won(it.supplyAmountNet)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50">
          <tr className="border-t"><td colSpan={6} className="px-2 py-1 text-right">공급가</td><td className="px-2 py-1 text-right">{won(d.supplyTotal)}</td></tr>
          <tr><td colSpan={6} className="px-2 py-1 text-right">부가세</td><td className="px-2 py-1 text-right">{won(d.vat)}</td></tr>
          <tr><td colSpan={6} className="px-2 py-1 text-right font-semibold">합계</td><td className="px-2 py-1 text-right font-semibold">{won(d.total)}</td></tr>
        </tfoot>
      </table>
      {d.customerRemark && <div className="mt-1 text-xs"><span className="text-gray-500">고객 비고 </span>{d.customerRemark}</div>}
      {d.adminMemo && <div className="mt-1 text-xs"><span className="text-gray-500">관리자 메모(고객 비노출) </span>{d.adminMemo}</div>}
    </div>
  );
};


export default function SettlementHistoryTable({ channelId, history, highlightMonth, compareMonth }) {
  // 열린 정산서 id 1개 + id 별 조회 결과({loading, data, error}) 캐시.
  const [openHistoryId, setOpenHistoryId] = useState(null);
  const [historyDetail, setHistoryDetail] = useState({});

  // 재조회·채널 변경 시 펼친 정산서를 닫고 캐시를 비운다 — 앞 채널의 내역이 남거나, 재생성 전 항목이 보이면 안 된다.
  useEffect(() => {
    setOpenHistoryId(null);
    setHistoryDetail({});
  }, [channelId, history]);

  // 행 클릭 → 그 정산서 항목을 펼친다(처음 열 때 1회 조회, 이후 캐시).
  const toggleHistory = (h) => {
    if (openHistoryId === h.id) { setOpenHistoryId(null); return; }
    setOpenHistoryId(h.id);
    if (historyDetail[h.id]?.data || historyDetail[h.id]?.loading) return;
    setHistoryDetail((m) => ({ ...m, [h.id]: { loading: true } }));
    getSettlement(channelId, h.billingMonth)
      .then((r) => setHistoryDetail((m) => ({ ...m, [h.id]: { data: r?.data ?? null } })))
      .catch((e) => setHistoryDetail((m) => ({ ...m, [h.id]: { error: e?.message ?? '조회 실패' } })));
  };

  const rows = history ?? [];

  return (
    <table className="min-w-full text-sm border">
      <thead className="bg-gray-50 text-xs">
        <tr className="divide-x">
          <th className="px-2 py-1">이용월</th>
          <th className="px-2 py-1">정산서</th>
          <th className="px-2 py-1">결제</th>
          <th className="px-2 py-1">청구 방식</th>
          <th className="px-2 py-1 text-right">공급가</th>
          <th className="px-2 py-1 text-right">부가세</th>
          <th className="px-2 py-1 text-right">합계</th>
          <th className="px-2 py-1 w-8" />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td className="px-2 py-3 text-center text-gray-400" colSpan={8}>정산 이력이 없습니다.</td></tr>}
        {rows.map((h) => {
          const pay = paymentView(h);
          const open = openHistoryId === h.id;
          const isCurrent = highlightMonth && h.billingMonth === highlightMonth;
          const isCompare = compareMonth && h.billingMonth === compareMonth;
          return (
            <React.Fragment key={h.id}>
              <tr
                className={`border-b divide-x cursor-pointer hover:bg-blue-50 ${open ? 'bg-blue-50' : isCurrent ? 'bg-yellow-50' : ''}`}
                onClick={() => toggleHistory(h)}
                title="눌러서 정산서 항목 보기"
              >
                <td className="px-2 py-1 font-medium whitespace-nowrap">
                  {h.billingMonth}
                  {isCurrent && <span className="ml-1 px-1 py-0.5 rounded text-[10px] bg-yellow-200 text-yellow-900">작업 중</span>}
                  {isCompare && <span className="ml-1 px-1 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">전월</span>}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${SETTLEMENT_STATUS_BADGE[h.status] ?? 'bg-gray-100'}`}>
                    {SETTLEMENT_STATUS_LABEL[h.status] ?? h.status}
                  </span>
                  {h.confirmedAt && (
                    <div className="text-xs text-gray-500 mt-0.5" title={h.confirmedBy ? `확정자 ${h.confirmedBy}` : ''}>
                      확정 {h.confirmedAt}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1 whitespace-nowrap" title={pay.title ?? ''}>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${pay.cls}`}>{pay.label}</span>
                  {pay.sub && <div className="text-xs text-gray-500 mt-0.5">{pay.sub}</div>}
                </td>
                <td className="px-2 py-1">{h.billingMethod === 'TAX_INVOICE' ? '세금계산서' : '카드'}</td>
                <td className="px-2 py-1 text-right">{won(h.supplyTotal)}</td>
                <td className="px-2 py-1 text-right">{won(h.vat)}</td>
                <td className="px-2 py-1 text-right font-semibold">{won(h.total)}</td>
                <td className="px-2 py-1 text-center text-gray-400">{open ? '▾' : '▸'}</td>
              </tr>
              {open && (
                <tr className="border-b">
                  <td colSpan={8} className="px-3 py-2 bg-gray-50">
                    <SettlementDetail state={historyDetail[h.id]} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
